import { buildPath } from '@rentspree/path'
import { useState, useEffect, useContext } from 'react'

import { AuthProviderContext } from '../containers/auth'
import env from '../env/next-env'

import { authService } from './auth-service'

/**
 * Hook to check if the current user session is authenticated
 * @returns {boolean} isAuthenticated - Whether the current user session is authenticated
 * @returns {boolean} isLoading - Whether the authentication check is in progress
 * @returns {Error | null} error - Error that occurred during authentication check, if any
 */
export const useAuth = (): {
  isAuthenticated: boolean
  isLoading: boolean
  error: Error | null
  redirectToLogin: () => void
} => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const { isAuthReady } = useContext(AuthProviderContext)

  useEffect(() => {
    const checkAuthentication = async (): Promise<void> => {
      try {
        setIsLoading(true)
        const authenticated = await authService.isAuthenticated()
        setIsAuthenticated(isAuthReady && authenticated)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to check authentication'))
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }

    void checkAuthentication()
  }, [isAuthReady])

  const redirectToLogin = () => {
    window.location.assign(
      buildPath(
        `${env.BASE_URL}/account/login`,
        {},
        { continue: encodeURI(window.location.href), userType: 'applicant' },
      ),
    )
  }

  return { isAuthenticated, isLoading, error, redirectToLogin }
}
